import React, { useState, useEffect, useCallback } from 'react';
import { Mail, UserPlus, X, RefreshCw, ChevronLeft, ChevronRight, Clock, CheckCircle2, XCircle, Ban } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { invitationService, Invitation, InvitationRole } from '../../services/invitationService';
import { getUserOrganizations } from '../../services/organizationService';
import { useUser } from '../../contextapi/UserContext';
import { supabase } from '../../utils/supabaseClient';

const STATUS_LABELS: Record<string, string> = {
    pending: 'Pending',
    accepted: 'Accepted',
    rejected: 'Rejected',
    revoked: 'Revoked',
    expired: 'Expired',
};

const STATUS_COLORS: Record<string, string> = {
    pending: 'text-yellow-600 bg-yellow-50 border-yellow-200',
    accepted: 'text-green-600 bg-green-50 border-green-200',
    rejected: 'text-red-600 bg-red-50 border-red-200',
    revoked: 'text-gray-500 bg-gray-50 border-gray-200',
    expired: 'text-orange-500 bg-orange-50 border-orange-200',
};

const StatusIcon = ({ status }: { status: string }) => {
    if (status === 'pending') return <Clock className="w-3 h-3" />;
    if (status === 'accepted') return <CheckCircle2 className="w-3 h-3" />;
    if (status === 'revoked') return <Ban className="w-3 h-3" />;
    return <XCircle className="w-3 h-3" />;
};

export const InvitationsSettings: React.FC = () => {
    const { toast } = useToast();
    const [orgId, setOrgId] = useState<string | null>(null);
    const [invitations, setInvitations] = useState<Invitation[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const pageSize = 10;
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [revoking, setRevoking] = useState<string | null>(null);

    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState<InvitationRole>('member');
    const [orgRoles, setOrgRoles] = useState<string[]>([]);

    const { user } = useUser();

    /**
     * The roles offered must be the organisation's own, not a hardcoded trio.
     *
     * Permissions are stored per (org_id, role) in org_role_permissions, so an admin who creates
     * a role such as "bv_member" has to be able to invite into it. The old list also offered
     * "viewer", which no organisation configures — inviting someone as Viewer gave them a
     * membership with zero permissions and no way to fix it short of an admin editing the row.
     *
     * Admin-only endpoint, which is fine: this whole panel is only reachable by an org admin.
     * On failure we keep the built-in admin/member pair rather than block inviting entirely.
     */
    useEffect(() => {
        if (!orgId || !user?.id) return;
        let cancelled = false;

        (async () => {
            try {
                const { data: { session } } = await supabase.auth.getSession();
                const res = await fetch(`/api/scopingai/permissions/roles?orgId=${orgId}`, {
                    headers: {
                        'x-user-id': user.id,
                        'x-org-id': orgId,
                        'x-api-key': (import.meta as any).env?.VITE_API_KEY || 'scopingai',
                        Authorization: session?.access_token ? `Bearer ${session.access_token}` : '',
                    },
                });
                const json = await res.json();
                if (!cancelled && json.success && Array.isArray(json.data)) {
                    setOrgRoles(json.data);
                }
            } catch (err) {
                console.error('Failed to load organisation roles:', err);
            }
        })();

        return () => { cancelled = true; };
    }, [orgId, user?.id]);

    // Load org
    useEffect(() => {
        getUserOrganizations().then(({ organizations, membership }) => {
            const adminMembership = membership.find(m => m.role === 'admin');
            if (adminMembership) setOrgId(adminMembership.organisation_id);
        }).catch(console.error);
    }, []);

    const loadInvitations = useCallback(async () => {
        if (!orgId) return;
        setLoading(true);
        try {
            const result = await invitationService.listInvitations(orgId, page, pageSize);
            setInvitations(result.invitations);
            setTotal(result.total);
        } catch (err) {
            console.error('Failed to load invitations:', err);
        } finally {
            setLoading(false);
        }
    }, [orgId, page]);

    useEffect(() => {
        loadInvitations();
    }, [loadInvitations]);

    const handleSendInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!orgId || !inviteEmail.trim()) return;
        setSending(true);
        try {
            await invitationService.sendInvite(orgId, inviteEmail.trim(), inviteRole);
            toast({ title: 'Invitation created', description: `${inviteEmail} can now sign up at /signup` });
            setInviteEmail('');
            setInviteRole('member');
            setPage(1);
            await loadInvitations();
        } catch (err: unknown) {
            const msg = (err as any)?.response?.data?.error || 'Failed to send invitation';
            toast({ title: 'Error', description: msg, variant: 'destructive' });
        } finally {
            setSending(false);
        }
    };

    const handleRevoke = async (id: string) => {
        setRevoking(id);
        try {
            await invitationService.revokeInvitation(id);
            toast({ title: 'Invitation revoked' });
            await loadInvitations();
        } catch (err: unknown) {
            const msg = (err as any)?.response?.data?.error || 'Failed to revoke invitation';
            toast({ title: 'Error', description: msg, variant: 'destructive' });
        } finally {
            setRevoking(null);
        }
    };

    const totalPages = Math.ceil(total / pageSize);

    return (
        <div className="space-y-6">
            <div className="dashboard-card p-6">
                <h2 className="text-xl font-semibold mb-1 flex items-center gap-2">
                    <Mail className="w-5 h-5 text-primary" />
                    Invite Team Members
                </h2>
                <p className="text-sm text-muted-foreground mb-5">
                    Send email invitations to add users to your organisation.
                </p>

                <form onSubmit={handleSendInvite} className="flex flex-col sm:flex-row gap-3">
                    <input
                        type="email"
                        placeholder="colleague@company.com"
                        value={inviteEmail}
                        onChange={e => setInviteEmail(e.target.value)}
                        required
                        className="flex-1 px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <select
                        value={inviteRole}
                        onChange={e => setInviteRole(e.target.value as InvitationRole)}
                        className="px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        {['admin', 'member', ...orgRoles]
                            .filter((role, i, all) => all.indexOf(role) === i)
                            .map(role => (
                                <option key={role} value={role}>{role}</option>
                            ))}
                    </select>
                    <button
                        type="submit"
                        disabled={sending || !inviteEmail.trim()}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {sending ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                            <UserPlus className="w-4 h-4" />
                        )}
                        {sending ? 'Sending...' : 'Send Invite'}
                    </button>
                </form>
            </div>

            <div className="dashboard-card p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                        Invitations
                        {total > 0 && (
                            <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                                {total}
                            </span>
                        )}
                    </h3>
                    <button
                        onClick={loadInvitations}
                        disabled={loading}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        title="Refresh"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {loading && invitations.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">Loading...</div>
                ) : invitations.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                        No invitations yet. Send your first invite above.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {invitations.map(inv => (
                            <div
                                key={inv.id}
                                className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <Mail className="w-4 h-4 text-primary" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium truncate">{inv.email}</p>
                                        <p className="text-xs text-muted-foreground">
                                            Role: <span className="capitalize">{inv.role}</span>
                                            {' · '}
                                            {new Date(inv.invited_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS_COLORS[inv.status] ?? ''}`}>
                                        <StatusIcon status={inv.status} />
                                        {STATUS_LABELS[inv.status] ?? inv.status}
                                    </span>
                                    {inv.status === 'pending' && (
                                        <button
                                            onClick={() => handleRevoke(inv.id)}
                                            disabled={revoking === inv.id}
                                            className="text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                                            title="Revoke invitation"
                                        >
                                            {revoking === inv.id ? (
                                                <RefreshCw className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <X className="w-4 h-4" />
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                        <p className="text-xs text-muted-foreground">
                            Page {page} of {totalPages} · {total} total
                        </p>
                        <div className="flex gap-1">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="p-1.5 rounded border text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="p-1.5 rounded border text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
